import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipProps,
} from "recharts";
import {
  BREAKDOWN_TIMEOUT_MS,
  TRAFFIC_RANGES,
  isBreakdownComplete,
  isBreakdownRunning,
  trafficBreakdownQueryKey,
  trafficBreakdownQueryOptions,
  trafficOverviewQueryOptions,
  type TrafficBreakdownComplete,
  type TrafficBreakdownRow,
  type TrafficDimension,
  type TrafficOverview,
  type TrafficPoint,
  type TrafficRange,
} from "@/lib/traffic.queries";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, type ChartConfig } from "@/components/ui/chart";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { FileClock, Loader2, RefreshCw, Table2 } from "lucide-react";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/admin/traffic")({
  component: TrafficPage,
});

// ---------------------------------------------------------------------------
// Chart palette. Validated with the dataviz palette script (light on #ffffff,
// dark on #1a1a19): the pair passes lightness, chroma, CVD, normal-vision and
// contrast checks in both modes. Requests, visitors and the 4xx line share
// slot 1 (blue); the 5xx line is slot 2 (orange). Colors never change by rank.
// ---------------------------------------------------------------------------
const SLOT_1 = { light: "#2a78d6", dark: "#3987e5" };
const SLOT_2 = { light: "#eb6834", dark: "#d95926" };
// Same slots as Tailwind classes, for marks drawn outside a ChartContainer.
const SLOT_1_BG = "bg-[#2a78d6] dark:bg-[#3987e5]";
const SLOT_2_BG = "bg-[#eb6834] dark:bg-[#d95926]";

const chartConfig = {
  requests: { label: "Requests", theme: SLOT_1 },
  visitors: { label: "Visitors", theme: SLOT_1 },
  err4xxRate: { label: "4xx rate", theme: SLOT_1 },
  err5xxRate: { label: "5xx rate", theme: SLOT_2 },
} satisfies ChartConfig;

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------
const compactFmt = new Intl.NumberFormat(undefined, {
  notation: "compact",
  maximumFractionDigits: 1,
});
const fullFmt = new Intl.NumberFormat(undefined);

const fmtCompact = (n: number) => compactFmt.format(n);
const fmtInt = (n: number) => fullFmt.format(Math.round(n));

function fmtBytes(bytes: number) {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  if (bytes >= 1e12) return `${(bytes / 1e12).toFixed(2)} TB`;
  if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(2)} GB`;
  if (bytes >= 1e6) return `${(bytes / 1e6).toFixed(1)} MB`;
  if (bytes >= 1e3) return `${(bytes / 1e3).toFixed(0)} KB`;
  return `${Math.round(bytes)} B`;
}

/** Rates arrive as percentages (0-100), the way CloudWatch reports CloudFront error rates. */
function fmtPct(pct: number) {
  if (!Number.isFinite(pct)) return "-";
  if (pct === 0) return "0%";
  if (pct >= 10) return `${pct.toFixed(1)}%`;
  return `${pct.toFixed(2)}%`;
}

const fmtDay = (iso: string) => format(new Date(iso), "MMM d");
const fmtDayTime = (iso: string) => format(new Date(iso), "MMM d, HH:mm");
const fmtLongDate = (iso: string) => format(new Date(iso), "MMM d, yyyy");

const isIsoDay = (s: string) => /^\d{4}-\d{2}-\d{2}/.test(s) && !Number.isNaN(Date.parse(s));

const RANGE_LABEL: Record<TrafficRange, string> = {
  "24h": "24 hours",
  "7d": "7 days",
  "30d": "30 days",
  "90d": "90 days",
};

const DIMENSIONS: Array<{
  value: TrafficDimension;
  label: string;
  keyHeader: string;
  description: string;
}> = [
  { value: "pages", label: "Pages", keyHeader: "Page", description: "Most requested paths." },
  {
    value: "referrers",
    label: "Referrers",
    keyHeader: "Referrer",
    description: "Where visitors came from. Direct visits and empty referrers are grouped.",
  },
  {
    value: "visitors",
    label: "Visitors",
    keyHeader: "Day",
    description:
      "Distinct visitors per day, counted by IP address. Bots are left out where they can be identified.",
  },
  {
    value: "agents",
    label: "Browsers and bots",
    keyHeader: "Browser or bot",
    description: "Browsers and known crawlers, by request count.",
  },
  {
    value: "countries",
    label: "Countries",
    keyHeader: "Country",
    description: "Page views by visitor country, from the country code CloudFront records.",
  },
  {
    value: "status",
    label: "Status codes",
    keyHeader: "Status",
    description: "HTTP status codes CloudFront returned to visitors.",
  },
  {
    value: "edges",
    label: "Edge locations",
    keyHeader: "Edge location",
    description:
      "CloudFront edge locations that served the requests, a rough map of where visitors are.",
  },
];

const STATUS_TEXT: Record<string, string> = {
  "200": "OK",
  "204": "No content",
  "206": "Partial content",
  "301": "Moved permanently",
  "302": "Found (redirect)",
  "304": "Not modified",
  "400": "Bad request",
  "403": "Forbidden",
  "404": "Not found",
  "405": "Method not allowed",
  "429": "Too many requests",
  "500": "Server error",
  "502": "Bad gateway",
  "503": "Service unavailable",
  "504": "Gateway timeout",
};

function rowLabel(dimension: TrafficDimension, key: string) {
  if (dimension === "referrers" && (key === "" || key === "-")) return "(direct or none)";
  if (dimension === "status") return STATUS_TEXT[key] ? `${key} ${STATUS_TEXT[key]}` : key;
  if (dimension === "visitors") return isIsoDay(key) ? fmtDay(key) : key;
  return key || "(empty)";
}

// ---------------------------------------------------------------------------
// Small shared pieces
// ---------------------------------------------------------------------------
function StatTile({ label, value, caption }: { label: string; value: string; caption: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="mt-1 text-2xl font-semibold leading-tight">{value}</p>
        <p className="mt-1 text-xs text-muted-foreground">{caption}</p>
      </CardContent>
    </Card>
  );
}

/** Legend entry: a short 2px stroke in the series color, name in text ink. */
function LineKey({ className, label }: { className: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
      <span aria-hidden className={cn("inline-block h-0.5 w-4 rounded-full", className)} />
      {label}
    </span>
  );
}

type TipPayloadItem = {
  dataKey?: string | number;
  name?: string | number;
  value?: number | string;
  color?: string;
  payload?: Record<string, unknown>;
};

/**
 * One tooltip, every series at that X. Values lead (strong ink), the series
 * name follows, keyed by a short stroke in the series color.
 */
function SeriesTooltip({
  active,
  payload,
  title,
  formatValue,
}: Pick<TooltipProps<number, string>, "active" | "payload"> & {
  title: (row: Record<string, unknown>) => string;
  formatValue: (key: string, value: number) => string;
}) {
  if (!active || !payload?.length) return null;
  const items = payload as TipPayloadItem[];
  const row = (items[0]?.payload ?? {}) as Record<string, unknown>;
  return (
    <div className="min-w-[9rem] rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl">
      <div className="mb-1 font-medium text-foreground">{title(row)}</div>
      <div className="grid gap-1">
        {items.map((it) => {
          const key = String(it.dataKey ?? it.name ?? "");
          const label = chartConfig[key as keyof typeof chartConfig]?.label ?? key;
          const v = typeof it.value === "number" ? it.value : Number(it.value ?? 0);
          return (
            <div key={key} className="flex items-center gap-2">
              <span
                aria-hidden
                className="inline-block h-0.5 w-3 shrink-0 rounded-full"
                style={{ background: it.color ?? `var(--color-${key})` }}
              />
              <span className="font-semibold tabular-nums text-foreground">
                {formatValue(key, v)}
              </span>
              <span className="text-muted-foreground">{label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EmptyNote({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof FileClock;
  title: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed px-4 py-10 text-center">
      <Icon className="h-6 w-6 text-muted-foreground" />
      <p className="text-sm font-medium">{title}</p>
      {children && <p className="max-w-md text-xs text-muted-foreground">{children}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Overview: KPI row + time series
// ---------------------------------------------------------------------------
const DAY_MS = 24 * 60 * 60 * 1000;

function useBucketLabels(series: TrafficPoint[]) {
  return useMemo(() => {
    // Sub-day buckets need the time in labels; daily buckets only the date.
    const subDay = series.length >= 2 && Date.parse(series[1].t) - Date.parse(series[0].t) < DAY_MS;
    // With hourly buckets across several days the same HH:mm repeats, so add the day.
    const spansDays =
      series.length >= 2 &&
      Date.parse(series[series.length - 1].t) - Date.parse(series[0].t) > DAY_MS;
    const tick = (iso: string) => {
      const d = new Date(iso);
      if (!subDay) return format(d, "MMM d");
      return spansDays ? format(d, "MMM d HH:mm") : format(d, "HH:mm");
    };
    const full = (iso: string) => (subDay ? fmtDayTime(iso) : fmtDay(iso));
    return { tick, full };
  }, [series]);
}

const AXIS_PROPS = { tickLine: false, axisLine: false, tickMargin: 8 } as const;
const ACTIVE_DOT = { r: 4, strokeWidth: 2, stroke: "var(--color-background)" } as const;
const CURSOR_LINE = { stroke: "var(--color-border)", strokeWidth: 1 } as const;

function RequestsChart({ series }: { series: TrafficPoint[] }) {
  const labels = useBucketLabels(series);
  return (
    <ChartContainer config={chartConfig} className="aspect-auto h-64 w-full">
      <AreaChart data={series} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
        <CartesianGrid vertical={false} stroke="#ccc" />
        <XAxis dataKey="t" {...AXIS_PROPS} minTickGap={40} tickFormatter={labels.tick} />
        <YAxis {...AXIS_PROPS} width={44} allowDecimals={false} tickFormatter={fmtCompact} />
        <Tooltip
          cursor={CURSOR_LINE}
          isAnimationActive={false}
          content={
            <SeriesTooltip
              title={(row) => labels.full(String(row.t))}
              formatValue={(_k, v) => fmtInt(v)}
            />
          }
        />
        <Area
          type="monotone"
          dataKey="requests"
          stroke="var(--color-requests)"
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
          fill="var(--color-requests)"
          fillOpacity={0.1}
          dot={false}
          activeDot={ACTIVE_DOT}
          isAnimationActive={false}
        />
      </AreaChart>
    </ChartContainer>
  );
}

function ErrorRateChart({ series }: { series: TrafficPoint[] }) {
  const labels = useBucketLabels(series);
  return (
    <ChartContainer config={chartConfig} className="aspect-auto h-40 w-full">
      <LineChart data={series} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
        <CartesianGrid vertical={false} stroke="#ccc" />
        <XAxis dataKey="t" {...AXIS_PROPS} minTickGap={40} tickFormatter={labels.tick} />
        <YAxis {...AXIS_PROPS} width={44} tickFormatter={(v: number) => `${v}%`} />
        <Tooltip
          cursor={CURSOR_LINE}
          isAnimationActive={false}
          content={
            <SeriesTooltip
              title={(row) => labels.full(String(row.t))}
              formatValue={(_k, v) => fmtPct(v)}
            />
          }
        />
        <Line
          type="monotone"
          dataKey="err4xxRate"
          stroke="var(--color-err4xxRate)"
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
          dot={false}
          activeDot={ACTIVE_DOT}
          isAnimationActive={false}
        />
        <Line
          type="monotone"
          dataKey="err5xxRate"
          stroke="var(--color-err5xxRate)"
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
          dot={false}
          activeDot={ACTIVE_DOT}
          isAnimationActive={false}
        />
      </LineChart>
    </ChartContainer>
  );
}

const NUM_CELL = "py-1.5 text-right text-xs tabular-nums";

/** Table twin of the time-series charts, so every plotted value is readable without hovering. */
function SeriesTable({ series }: { series: TrafficPoint[] }) {
  const labels = useBucketLabels(series);
  return (
    <div className="max-h-72 overflow-auto rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Bucket start</TableHead>
            <TableHead className="text-right">Requests</TableHead>
            <TableHead className="text-right">Data</TableHead>
            <TableHead className="text-right">4xx</TableHead>
            <TableHead className="text-right">5xx</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {series.map((p) => (
            <TableRow key={p.t}>
              <TableCell className="py-1.5 text-xs">{labels.full(p.t)}</TableCell>
              <TableCell className={NUM_CELL}>{fmtInt(p.requests)}</TableCell>
              <TableCell className={NUM_CELL}>{fmtBytes(p.bytes)}</TableCell>
              <TableCell className={NUM_CELL}>{fmtPct(p.err4xxRate)}</TableCell>
              <TableCell className={NUM_CELL}>{fmtPct(p.err5xxRate)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function OverviewSection({
  data,
  range,
  dimmed,
}: {
  data: TrafficOverview;
  range: TrafficRange;
  dimmed: boolean;
}) {
  const [showTable, setShowTable] = useState(false);
  const caption = `Last ${RANGE_LABEL[range]}`;
  const t = data.totals;
  const hasSeries = data.series.length > 0;

  return (
    <div className={cn("space-y-4 transition-opacity", dimmed && "opacity-60")}>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label="Requests"
          value={fmtCompact(t.requests)}
          caption={`${fmtInt(t.requests)} · ${caption}`}
        />
        <StatTile
          label="Data transferred"
          value={fmtBytes(t.bytes)}
          caption={`To visitors · ${caption}`}
        />
        <StatTile
          label="4xx rate"
          value={fmtPct(t.err4xxRate)}
          caption="Client errors: not found, forbidden"
        />
        <StatTile
          label="5xx rate"
          value={fmtPct(t.err5xxRate)}
          caption="Server errors: origin failures"
        />
      </div>
      <p className="text-xs text-muted-foreground">
        Requests, data and error rates come from CloudFront metrics in CloudWatch. The detail
        section below comes from CloudFront access logs. Visitor counts are approximate: distinct IP
        addresses, with bots left out where they can be identified.
      </p>

      <Card>
        <CardHeader className="flex flex-col gap-2 pb-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle className="text-base">Requests over time</CardTitle>
            <CardDescription>
              {hasSeries
                ? `${fmtDayTime(data.from)} to ${fmtDayTime(data.to)} · ${data.series.length} buckets`
                : "No data points for this range."}
            </CardDescription>
          </div>
          {hasSeries && (
            <Button variant="ghost" size="sm" onClick={() => setShowTable((v) => !v)}>
              <Table2 className="mr-1 h-4 w-4" />
              {showTable ? "Hide table" : "Show table"}
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {!hasSeries ? (
            <EmptyNote icon={FileClock} title="No traffic recorded in this range">
              CloudWatch has no CloudFront data points for the selected period.
            </EmptyNote>
          ) : (
            <>
              <RequestsChart series={data.series} />
              <div>
                <div className="mb-1 flex flex-wrap items-center gap-x-4 gap-y-1">
                  <span className="text-sm font-medium">Error rates</span>
                  <LineKey className={SLOT_1_BG} label="4xx rate" />
                  <LineKey className={SLOT_2_BG} label="5xx rate" />
                </div>
                <ErrorRateChart series={data.series} />
              </div>
              {showTable && <SeriesTable series={data.series} />}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Breakdown from access logs
// ---------------------------------------------------------------------------
function VisitorsChart({ rows }: { rows: TrafficBreakdownRow[] }) {
  const data = useMemo(
    () =>
      rows
        .filter((r) => isIsoDay(r.key))
        .sort((a, b) => a.key.localeCompare(b.key))
        .map((r) => ({ key: r.key, visitors: r.visitors ?? 0, requests: r.requests })),
    [rows],
  );
  if (data.length === 0) return null;
  return (
    <ChartContainer config={chartConfig} className="aspect-auto h-52 w-full">
      <BarChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }} barCategoryGap="20%">
        <CartesianGrid vertical={false} stroke="#ccc" />
        <XAxis dataKey="key" {...AXIS_PROPS} minTickGap={24} tickFormatter={fmtDay} />
        <YAxis {...AXIS_PROPS} width={44} allowDecimals={false} tickFormatter={fmtCompact} />
        <Tooltip
          cursor={{ fill: "var(--color-muted)" }}
          isAnimationActive={false}
          content={
            <SeriesTooltip
              title={(row) => fmtLongDate(String(row.key))}
              formatValue={(_k, v) => fmtInt(v)}
            />
          }
        />
        <Bar
          dataKey="visitors"
          fill="var(--color-visitors)"
          maxBarSize={24}
          radius={[4, 4, 0, 0]}
          isAnimationActive={false}
        />
      </BarChart>
    </ChartContainer>
  );
}

function BreakdownTable({
  dimension,
  keyHeader,
  rows,
}: {
  dimension: TrafficDimension;
  keyHeader: string;
  rows: TrafficBreakdownRow[];
}) {
  const hasVisitors = rows.some((r) => typeof r.visitors === "number");
  const total = rows.reduce((s, r) => s + r.requests, 0) || 1;
  const sorted = useMemo(
    () =>
      dimension === "visitors"
        ? [...rows].sort((a, b) => b.key.localeCompare(a.key))
        : [...rows].sort((a, b) => b.requests - a.requests),
    [rows, dimension],
  );

  return (
    <div className="max-h-[28rem] overflow-auto rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{keyHeader}</TableHead>
            <TableHead className="w-28 text-right">Requests</TableHead>
            <TableHead className="hidden w-44 sm:table-cell">Share</TableHead>
            {hasVisitors && <TableHead className="w-24 text-right">Visitors</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((r) => {
            const share = (r.requests / total) * 100;
            return (
              <TableRow key={r.key}>
                <TableCell className="max-w-[28rem] truncate py-1.5 text-xs" title={r.key}>
                  {rowLabel(dimension, r.key)}
                </TableCell>
                <TableCell className={NUM_CELL}>{fmtInt(r.requests)}</TableCell>
                <TableCell className="hidden py-1.5 sm:table-cell">
                  <div className="flex items-center gap-2">
                    <div className="h-2 flex-1 overflow-hidden rounded-sm bg-muted">
                      <div
                        className={cn("h-full rounded-sm", SLOT_1_BG)}
                        style={{ width: `${Math.max(share, 0.5)}%` }}
                      />
                    </div>
                    <span className="w-12 text-right text-xs tabular-nums text-muted-foreground">
                      {share < 0.05 ? "<0.1%" : `${share.toFixed(1)}%`}
                    </span>
                  </div>
                </TableCell>
                {hasVisitors && (
                  <TableCell className={NUM_CELL}>
                    {typeof r.visitors === "number" ? fmtInt(r.visitors) : "-"}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function BreakdownResult({
  data,
  dimension,
  keyHeader,
  dimmed,
}: {
  data: TrafficBreakdownComplete;
  dimension: TrafficDimension;
  keyHeader: string;
  dimmed: boolean;
}) {
  if (data.rows.length === 0 && data.logsFrom == null) {
    return (
      <EmptyNote icon={FileClock} title="Access logs are not collecting yet">
        Once CloudFront access logging is on, this section will list top pages, referrers, daily
        visitors, browsers and bots, status codes and edge locations for the selected range, usually
        within an hour of the first requests.
      </EmptyNote>
    );
  }
  return (
    <div className={cn("space-y-3 transition-opacity", dimmed && "opacity-60")}>
      {data.rows.length === 0 ? (
        <EmptyNote icon={FileClock} title="No log entries in this range">
          {data.logsFrom
            ? `Logs have been collected since ${fmtLongDate(data.logsFrom)}; nothing matched this range yet.`
            : undefined}
        </EmptyNote>
      ) : (
        <>
          {dimension === "visitors" && <VisitorsChart rows={data.rows} />}
          <BreakdownTable dimension={dimension} keyHeader={keyHeader} rows={data.rows} />
        </>
      )}
      <p className="text-xs text-muted-foreground">
        {data.logsFrom ? `Logs collected since ${fmtLongDate(data.logsFrom)}. ` : ""}
        {data.sampled ? "Based on a sample of log lines, so counts are approximate. " : ""}
        {`${data.rows.length} ${data.rows.length === 1 ? "row" : "rows"}.`}
      </p>
    </div>
  );
}

function BreakdownSection({ range }: { range: TrafficRange }) {
  const qc = useQueryClient();
  const [dimension, setDimension] = useState<TrafficDimension>("pages");
  const meta = DIMENSIONS.find((d) => d.value === dimension) ?? DIMENSIONS[0];
  const q = useQuery(trafficBreakdownQueryOptions(range, dimension));
  const data = q.data;

  // The kept-previous data may belong to another tab or range; only show rows that match.
  const done = isBreakdownComplete(data) ? data : null;
  const complete = done && done.dimension === dimension && done.range === range ? done : null;
  const previousSameTab = done && !complete && done.dimension === dimension ? done : null;
  const running = isBreakdownRunning(data) ? data : null;
  const timedOut =
    !!running && !q.isFetching && Date.now() - running.startedAt >= BREAKDOWN_TIMEOUT_MS;
  const querying = q.isFetching || (!!running && !timedOut);

  const restart = () => qc.resetQueries({ queryKey: trafficBreakdownQueryKey(range, dimension) });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Detail from access logs</CardTitle>
        <CardDescription>
          Pages, referrers, visitors, browsers, status codes and edge locations for the selected
          range, read from CloudFront access logs.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Tabs value={dimension} onValueChange={(v) => setDimension(v as TrafficDimension)}>
          <TabsList className="h-auto flex-wrap justify-start">
            {DIMENSIONS.map((d) => (
              <TabsTrigger key={d.value} value={d.value}>
                {d.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <p className="text-xs text-muted-foreground">{meta.description}</p>

        {querying && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
            <Loader2 className="h-4 w-4 animate-spin" />
            Querying logs...
          </p>
        )}

        {q.isError && (
          <div className="flex flex-wrap items-center gap-3 text-sm text-red-700">
            <span>
              Could not load this breakdown:{" "}
              {q.error instanceof Error ? q.error.message : "unknown error"}
            </span>
            <Button variant="outline" size="sm" onClick={restart}>
              <RefreshCw className="mr-1 h-4 w-4" /> Try again
            </Button>
          </div>
        )}

        {timedOut && !q.isError && (
          <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
            <span>The log query is taking longer than a minute. It may still finish.</span>
            <Button variant="outline" size="sm" onClick={() => q.refetch()}>
              Check again
            </Button>
            <Button variant="ghost" size="sm" onClick={restart}>
              Start over
            </Button>
          </div>
        )}

        {complete && (
          <BreakdownResult
            data={complete}
            dimension={dimension}
            keyHeader={meta.keyHeader}
            dimmed={q.isFetching}
          />
        )}
        {!complete && previousSameTab && (
          // Same tab, different range: keep the old rows on screen, dimmed, until the new ones land.
          <BreakdownResult
            data={previousSameTab}
            dimension={dimension}
            keyHeader={meta.keyHeader}
            dimmed
          />
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
function TrafficPage() {
  const qc = useQueryClient();
  const [range, setRange] = useState<TrafficRange>("7d");
  const overview = useQuery(trafficOverviewQueryOptions(range));

  const refreshAll = () => {
    qc.invalidateQueries({ queryKey: ["traffic_overview"] });
    qc.resetQueries({ queryKey: ["traffic_breakdown"] });
  };

  const stale = overview.isPlaceholderData || (overview.isFetching && !!overview.data);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle>Traffic</CardTitle>
            <CardDescription>
              Requests, data transferred and errors for miinews.com, served through CloudFront. The
              range applies to everything on this page.
            </CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Tabs value={range} onValueChange={(v) => setRange(v as TrafficRange)}>
              <TabsList aria-label="Time range">
                {TRAFFIC_RANGES.map((r) => (
                  <TabsTrigger key={r} value={r}>
                    {r}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <Button variant="outline" size="sm" onClick={refreshAll} disabled={overview.isFetching}>
              <RefreshCw className={cn("mr-1 h-4 w-4", overview.isFetching && "animate-spin")} />
              Refresh
            </Button>
          </div>
        </CardHeader>
      </Card>

      {overview.isPending && (
        <Card>
          <CardContent className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading traffic for the last {RANGE_LABEL[range]}...
          </CardContent>
        </Card>
      )}

      {overview.isError && (
        <Card>
          <CardContent className="flex flex-wrap items-center gap-3 p-6 text-sm text-red-700">
            <span>
              Could not load traffic:{" "}
              {overview.error instanceof Error ? overview.error.message : "unknown error"}
            </span>
            <Button variant="outline" size="sm" onClick={() => overview.refetch()}>
              <RefreshCw className="mr-1 h-4 w-4" /> Try again
            </Button>
          </CardContent>
        </Card>
      )}

      {overview.data && <OverviewSection data={overview.data} range={range} dimmed={stale} />}

      <BreakdownSection range={range} />
    </div>
  );
}
